import { useEffect, useState, type FormEvent } from 'react';
import type { TestTask } from '../api/tests';
import { ca } from '../i18n/ca';
import { SLOW_START_MINUTES, type CardServerPhase } from './cardServerPhase';
import { GenerationNotice } from './GenerationNotice';

/** Whole minutes since `iso`, read again every ten seconds while `ticking`. */
function useMinutesSince(iso: string | null, ticking: boolean): number | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!ticking) return;
    const timer = window.setInterval(() => setNow(Date.now()), 10_000);
    return () => window.clearInterval(timer);
  }, [ticking]);
  if (!iso) return null;
  const then = Date.parse(iso);
  return Number.isNaN(then) ? null : Math.max(0, Math.floor((now - then) / 60_000));
}

const PHASE_ICON: Record<CardServerPhase, string> = {
  none: 'bi-power',
  waiting: 'bi-hourglass',
  starting: 'bi-arrow-repeat',
  ready: 'bi-check-circle-fill',
  ended: 'bi-stop-circle',
};

/**
 * A play card's server, in one box a tester cannot miss (the owner,
 * 2026-10-02: "de manera clara i intuïtiva"): whether it is off, starting or
 * up, the one button that starts it, how long it has been starting, and that
 * finishing the test stops it. Who may press: whoever took the card, or an
 * admin; anybody else is told who can. A card with no mission says so, and
 * gives an admin the box to name one.
 */
export function CardServer({
  task,
  phase,
  yours,
  isAdmin,
  canAct,
  ownSession,
  onStart,
  onAssign,
  onStop,
}: {
  task: TestTask;
  phase: CardServerPhase;
  /** Whoever took the card, or an admin. */
  yours: boolean;
  isAdmin: boolean;
  /** The card is still open or taken: something may still be started. */
  canAct: boolean;
  /** The session is the card's own (it has a mission): finishing the card stops its server. */
  ownSession: boolean;
  onStart: () => Promise<void>;
  onAssign: (missionCode: string) => Promise<void>;
  /** «Atura el servidor»: ends the card's own session and stops its server. */
  onStop?: () => Promise<void>;
}) {
  const t = ca.testsPage.server;
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState('');
  const minutes = useMinutesSince(task.server?.since ?? null, phase === 'starting');

  const start = () => {
    setBusy(true);
    void onStart().finally(() => setBusy(false));
  };
  // «Atura el servidor» (the owner, 2026-10-15): whenever the card's own server is
  // up, whatever the card's state, for whoever the platform says may (the card's
  // tester, its author, an admin). Somebody still heard in the game is asked about first.
  const stoppable =
    !!onStop &&
    ownSession &&
    (phase === 'starting' || phase === 'ready') &&
    task.server?.canStop === true;
  const stop = () => {
    if (!onStop) return;
    if (task.server?.inGame && !window.confirm(t.confirmStopInGame)) return;
    setBusy(true);
    void onStop().finally(() => setBusy(false));
  };
  const assign = (e: FormEvent) => {
    e.preventDefault();
    if (!code.trim()) return;
    setBusy(true);
    void onAssign(code.trim()).finally(() => setBusy(false));
  };

  // A session named and not read yet: nothing to say until it is.
  if (phase === 'none' && task.sessionCode) return null;

  // No mission and no session: nothing to start. The organiser names the mission here.
  if (!task.missionCode && phase === 'none') {
    return (
      <div className="server-panel state-none" role="group" aria-label={t.title}>
        <p className="server-line">
          <i className="bi bi-slash-circle" aria-hidden="true" />
          <strong>{t.noMission}</strong>
        </p>
        <p className="muted small">{isAdmin ? t.noMissionAdmin : t.noMissionHint}</p>
        {isAdmin && canAct && (
          <form className="row actions" onSubmit={assign}>
            <input
              className="control"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={t.missionCode}
              aria-label={t.missionCode}
            />
            <button type="submit" disabled={busy || !code.trim()}>
              {t.assign}
            </button>
          </form>
        )}
      </div>
    );
  }

  const startable = !!task.missionCode && canAct && (phase === 'none' || phase === 'ended');
  const headline =
    phase === 'none'
      ? t.off
      : phase === 'waiting'
        ? t.waiting
        : phase === 'starting'
          ? t.starting
          : phase === 'ready'
            ? t.ready
            : t.ended;
  return (
    <div className={`server-panel state-${phase}`} role="group" aria-label={t.title}>
      <p className="server-line" aria-live="polite">
        <i
          className={`bi ${PHASE_ICON[phase]}${phase === 'starting' ? ' spin' : ''}`}
          aria-hidden="true"
        />
        <strong>{headline}</strong>
        {phase === 'starting' && minutes !== null && (
          <span className="muted small">{t.elapsed(minutes)}</span>
        )}
      </p>

      {phase === 'starting' && (
        <>
          <div className="server-progress" aria-hidden="true" />
          <p className="muted small">{t.startingHint}</p>
          {minutes !== null && minutes >= SLOW_START_MINUTES && (
            <p className="warn small" role="note">
              {t.slow}
            </p>
          )}
        </>
      )}
      {phase === 'ready' && (
        <p className="muted small">
          {t.readyHint}{' '}
          {ownSession && (task.state === 'done' ? t.stopsWhenEmpty : canAct ? t.stopsOnDone : '')}
        </p>
      )}
      {stoppable && (
        <p className="row actions">
          <button type="button" onClick={stop} disabled={busy}>
            <i className="bi bi-stop-circle" aria-hidden="true" />
            {busy ? t.stopping : t.stop}
          </button>
          <span className="muted small">{t.stopHint}</span>
        </p>
      )}
      {phase === 'waiting' && <p className="muted small">{t.waitingHint}</p>}
      {phase === 'ended' && (
        <p className="muted small">{startable && yours ? t.endedHint : t.endedDone}</p>
      )}

      {startable &&
        (yours ? (
          <>
            {phase === 'none' && <p className="muted small">{t.offHint}</p>}
            {/* A mission generated weeks ago goes into the night without the rules added since: said before the server is asked for. */}
            {task.missionCode && <GenerationNotice code={task.missionCode} compact />}
            <p className="row actions">
              <button type="button" className="primary big" onClick={start} disabled={busy}>
                <i className="bi bi-play-fill" aria-hidden="true" />
                {busy ? t.requesting : phase === 'ended' ? t.restart : t.start}
              </button>
            </p>
          </>
        ) : (
          <p className="muted small">
            {task.state === 'open' ? t.claimFirst : t.notYours(task.claimedBy)}
          </p>
        ))}
    </div>
  );
}
